import { QueryClient } from '@tanstack/react-query';

/**
 * Invalidates all product-related queries to ensure data consistency across tabs.
 *
 * This should be called after any operation that modifies product data, such as:
 * - Product sync from WooCommerce
 * - Product creation, update, or deletion
 * - Bulk operations on products
 *
 * @param queryClient - The React Query client instance
 */
export function invalidateProductRelatedQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['products'] });
  queryClient.invalidateQueries({ queryKey: ['stores'] });
  queryClient.invalidateQueries({ queryKey: ['seo-issues'] });
  queryClient.invalidateQueries({ queryKey: ['seo-issue-counts'] });
  queryClient.invalidateQueries({ queryKey: ['products-stats'] });
  queryClient.invalidateQueries({ queryKey: ['issue-stats'] });
  queryClient.invalidateQueries({ queryKey: ['priority-actions'] });
  queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
  queryClient.invalidateQueries({ queryKey: ['store-settings'] });
}

/**
 * Invalidates category and menu queries when AI chat modifies them.
 * Called from ChatContext when AI operations return invalidate_queries hints.
 *
 * @param queryClient - The React Query client instance
 * @param queryKeys - Array of query keys to invalidate (e.g., ['store-categories', 'store-menus'])
 */
export function invalidateChatQueries(queryClient: QueryClient, queryKeys: string[]) {
  for (const key of queryKeys) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Invalidates all category-related queries.
 */
export function invalidateCategoryQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['store-categories'] });
  queryClient.invalidateQueries({ queryKey: ['categories-for-menu'] });
}

/**
 * Invalidates all menu-related queries.
 */
export function invalidateMenuQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['store-menus'] });
  queryClient.invalidateQueries({ queryKey: ['store-menu-items'] });
  queryClient.invalidateQueries({ queryKey: ['menu-hierarchy'] });
}
